import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { apiClient } from '@/lib/api/client';
import { useTenantStore } from '@/stores/tenantStore';

import { customizationKeys } from './customizationKeys';

// Wire shape for GET/PUT /workflows/{id}/customization (WorkflowCustomizationDto).
export const workflowCustomizationSchema = z.object({
  workflowId: z.string(),
  customizationJson: z.string().nullable(),
});

export type WorkflowCustomization = z.infer<typeof workflowCustomizationSchema>;

const wfKey = (tenantId: string | null, workflowId: string) =>
  [...customizationKeys.all, 'workflow', tenantId, workflowId] as const;

// GET a workflow's branding override (null = inherits the org default).
export function useGetWorkflowCustomization(workflowId: string | null) {
  const tenantId = useTenantStore((s) => s.tenantId);

  return useQuery({
    queryKey: wfKey(tenantId, workflowId ?? ''),
    queryFn: async ({ signal }) => {
      const raw = await apiClient
        .get(`api/v1/t/${tenantId}/workflows/${workflowId}/customization`, { signal })
        .json();
      return workflowCustomizationSchema.parse(raw);
    },
    enabled: !!tenantId && !!workflowId,
    staleTime: 60_000,
  });
}

// PUT a workflow's branding override (null clears it → inherit org default).
export function useSaveWorkflowCustomization() {
  const tenantId = useTenantStore((s) => s.tenantId);
  const queryClient = useQueryClient();

  return useMutation<
    WorkflowCustomization,
    Error,
    { workflowId: string; customizationJson: string | null }
  >({
    mutationFn: async ({ workflowId, customizationJson }) => {
      const raw = await apiClient
        .put(`api/v1/t/${tenantId}/workflows/${workflowId}/customization`, {
          json: { customizationJson },
        })
        .json();
      return workflowCustomizationSchema.parse(raw);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(wfKey(tenantId, data.workflowId), data);
    },
  });
}
